/**
 * ComposeSuccessView Component
 * Success confirmation with similar experts CTA and guest sign-up
 */

import { cn } from '@/lib/utils';
import { TYPOGRAPHY, SPACING } from '@/design-system';
import { RESPONSIVE } from '@/design-system/helpers/responsive';
import { LComponents, LCommon } from '@/components/i18n';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { UserSelectCard } from '@/components/UserSelectCard';
import { CheckCircle2, Loader2, UserPlus, Bell, MessageSquare, History } from 'lucide-react';
import type { Expert } from './types';

interface ComposeSuccessViewProps {
  sentExperts: Expert[];
  similarExperts: Expert[];
  selectedSimilarExperts: string[];
  onToggleSimilarExpert: (expertId: string) => void;
  onSendToSimilar: () => void;
  onClose: () => void;
  isSendingBulk: boolean;
  isLoadingSimilarExperts?: boolean;
  isGuest?: boolean;
  guestEmail?: string;
  onSignUp?: () => void;
}

export function ComposeSuccessView({
  sentExperts,
  similarExperts,
  selectedSimilarExperts,
  onToggleSimilarExpert,
  onSendToSimilar,
  onClose,
  isSendingBulk,
  isLoadingSimilarExperts = false,
  isGuest = false,
  guestEmail,
  onSignUp,
}: ComposeSuccessViewProps) {
  return (
    <div className={SPACING.stackXl}>
      {/* Success confirmation */}
      <div className={cn(
        RESPONSIVE.flexRowCenterGap3,
        SPACING.cardPadding,
        'bg-success/10 rounded-lg border border-success/30'
      )}>
        <CheckCircle2 className="h-6 w-6 text-success flex-shrink-0" />
        <div>
          <p className={cn(TYPOGRAPHY.cardTitleSmall, 'text-success')}>
            <LComponents 
              id="compose_message.success.sent_title" 
              values={{ count: sentExperts.length }} 
              defaultValue='Inquiry Sent' 
            />
          </p>
          <p className={TYPOGRAPHY.caption}>
            <LComponents id="compose_message.success.sent_description" defaultValue="Your inquiry is on its way." />
          </p>
        </div>
      </div>

      {/* Guest Sign-Up CTA */}
      {isGuest && onSignUp && (
        <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="h-5 w-5 text-primary" />
            <h4 className={TYPOGRAPHY.cardTitleSmall}>
              <LComponents id="compose_message.success.guest_cta.title" defaultValue="Create your free account" />
            </h4>
          </div>
          <ul className="text-sm text-muted-foreground mb-4 space-y-2">
            <li className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary/70" />
              <LComponents id="compose_message.success.guest_cta.benefit_notify" defaultValue="Get notified when experts respond" />
            </li>
            <li className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary/70" />
              <LComponents id="compose_message.success.guest_cta.benefit_followup" defaultValue="Send follow-up messages easily" />
            </li>
            <li className="flex items-center gap-2">
              <History className="h-4 w-4 text-primary/70" />
              <LComponents id="compose_message.success.guest_cta.benefit_history" defaultValue="Save your conversation history" />
            </li>
          </ul>
          <Button onClick={onSignUp} className="w-full">
            <LComponents 
              id="compose_message.success.guest_cta.signup_button" 
              values={{ email: guestEmail }} 
              defaultValue="Sign Up with {{email}}" 
            />
          </Button>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            <LComponents id="compose_message.success.guest_cta.signin_link" defaultValue="Already have an account?" />{' '}
            <button onClick={onSignUp} className="text-primary hover:underline">
              <LCommon id="actions.sign_in" defaultValue="Sign in" />
            </button>
          </p>
        </div>
      )}

      {/* Similar experts CTA */}
      <div className={SPACING.stackLg}>
        <div>
          <h4 className={TYPOGRAPHY.cardTitleSmall}>
            <LComponents id="compose_message.success.similar_experts_title" defaultValue="Contact More Experts" />
          </h4>
          <p className={TYPOGRAPHY.cardDescription}>
            <LComponents id="compose_message.success.similar_experts_description" defaultValue='Connect with other experts in your area who offer similar services.' />
          </p>
        </div>
        
        {isLoadingSimilarExperts ? (
          <div className={SPACING.stackMd}>
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        ) : similarExperts.length > 0 ? (
          <>
            <div className={SPACING.stackMd}>
              {similarExperts.map(expert => (
                <UserSelectCard
                  key={expert.id}
                  expert={expert}
                  selected={selectedSimilarExperts.includes(expert.id)}
                  onToggle={onToggleSimilarExpert}
                  matchScore={expert.matchScore}
                  matchReason={expert.matchReason}
                />
              ))}
            </div>
            
            {selectedSimilarExperts.length > 0 && (
              <Button onClick={onSendToSimilar} disabled={isSendingBulk} className="w-full">
                {isSendingBulk && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <LComponents 
                  id="compose_message.button.send_to_count" 
                  values={{ count: selectedSimilarExperts.length }} 
                  defaultValue='Send to {{count}} Expert(s)' 
                />
              </Button>
            )}
          </>
        ) : (
          <p className={TYPOGRAPHY.caption}>
            <LComponents id="compose_message.success.no_similar_experts" defaultValue="No additional experts found in your area." />
          </p>
        )}
      </div>

      {/* Done button */}
      <Button variant="outline" onClick={onClose} className="w-full">
        <LCommon id="actions.done" defaultValue="Done" />
      </Button>
    </div>
  );
}
